import { scrubValue } from "./sentry-privacy"

const BE_ERROR_CODE_PATTERN = /^BE-[A-Z]+-\d+$/

function readPlainObject(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== "object" || error instanceof Error) {
    return null
  }

  return error as Record<string, unknown>
}

function readMessage(error: unknown, plain: Record<string, unknown> | null) {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === "string" && error) return error

  if (plain) {
    if (typeof plain.message === "string" && plain.message) return plain.message
    if (typeof plain.detail === "string" && plain.detail) return plain.detail
    if (typeof plain.dev_message === "string" && plain.dev_message) {
      return plain.dev_message
    }
  }

  return "Unknown error"
}

function readErrorCode(
  plain: Record<string, unknown> | null,
  axiosCode?: string
) {
  const candidates = [plain?.code, plain?.error_code, axiosCode]

  for (const candidate of candidates) {
    if (typeof candidate !== "string" || !candidate) continue
    if (BE_ERROR_CODE_PATTERN.test(candidate)) return candidate
  }

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate) return candidate
  }

  return undefined
}

export function serializeError(error: unknown) {
  const plain = readPlainObject(error)
  const axiosLike = error as {
    isAxiosError?: boolean
    code?: string
    message?: string
    response?: {
      status?: number
      statusText?: string
      data?: unknown
    }
  }

  const message = readMessage(error, plain)
  const errorCode = readErrorCode(plain, axiosLike.code)
  const api =
    typeof plain?.api === "string"
      ? plain.api
      : typeof plain?.source === "string"
        ? plain.source
        : undefined

  const errorDetails =
    error instanceof Error
      ? Object.fromEntries(
          Object.entries(error as Error & Record<string, unknown>).filter(
            ([key]) => !["name", "message", "stack", "cause"].includes(key)
          )
        )
      : undefined

  return {
    name:
      error instanceof Error
        ? error.name
        : typeof plain?.name === "string"
          ? plain.name
          : errorCode ?? "ApplicationError",
    message,
    errorCode,
    api,
    detail: scrubValue(plain?.detail),
    isAxiosError: Boolean(axiosLike.isAxiosError),
    httpStatus: axiosLike.response?.status,
    httpStatusText: axiosLike.response?.statusText,
    axiosCode: axiosLike.isAxiosError ? axiosLike.code : undefined,
    response: scrubValue(axiosLike.response?.data ?? plain?.response),
    raw: plain ? scrubValue(plain) : undefined,
    details: scrubValue(errorDetails),
  }
}

export function formatFlowErrorMessage(
  feature: string,
  step: string,
  serialized: ReturnType<typeof serializeError>
) {
  const prefix = `[${feature}] ${step}`
  const codeSuffix = serialized.errorCode ? ` (${serialized.errorCode})` : ""
  return `${prefix}${codeSuffix}: ${serialized.message}`
}

export function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error

  const serialized = serializeError(error)
  const normalized = new Error(serialized.message)
  normalized.name = serialized.errorCode ?? serialized.name

  const plain = readPlainObject(error)
  if (plain?.cause) {
    ;(normalized as Error & { cause?: unknown }).cause = plain.cause
  }

  return normalized
}
